import {
  AiConversationsState,
  AiConversationsActionTypes,
  SET_CONVERSATION_ID,
  CLEAR_CONVERSATION_ID,
  CLEAR_ALL_CONVERSATIONS,
} from './aiConversationsTypes';
import * as actionTypes from 'constants/action-types';

const SESSION_STORAGE_KEY = 'aiConversationsCache';

// Helper function to check if sessionStorage is available
const isSessionStorageAvailable = (): boolean => {
  try {
    const test = '__sessionStorage_test__';
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

// Helper function to load state from sessionStorage
const loadFromSessionStorage = (): AiConversationsState => {
  if (!isSessionStorageAvailable()) {
    return { conversationCache: {} };
  }

  try {
    const serializedState = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (serializedState === null) {
      return { conversationCache: {} };
    }
    const parsedState = JSON.parse(serializedState);
    return {
      conversationCache: parsedState.conversationCache || {},
    };
  } catch (err) {
    console.warn('Failed to load AI conversations from sessionStorage:', err);
    return { conversationCache: {} };
  }
};

// Helper function to save state to sessionStorage
const saveToSessionStorage = (state: AiConversationsState): void => {
  if (!isSessionStorageAvailable()) {
    return;
  }

  try {
    const serializedState = JSON.stringify(state);
    sessionStorage.setItem(SESSION_STORAGE_KEY, serializedState);
  } catch (err) {
    console.warn('Failed to save AI conversations to sessionStorage:', err);
  }
};

// Initialize state from sessionStorage
const initialState: AiConversationsState = loadFromSessionStorage();

export const aiConversationsReducer = (
  state = initialState,
  action: AiConversationsActionTypes,
): AiConversationsState => {
  let newState: AiConversationsState;

  switch (action.type) {
    case SET_CONVERSATION_ID:
      newState = {
        ...state,
        conversationCache: {
          ...state.conversationCache,
          [action.payload.targetFieldId]: action.payload.conversationId,
        },
      };
      break;

    case CLEAR_CONVERSATION_ID:
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [action.payload.targetFieldId]: _, ...remainingCache } = state.conversationCache;
      newState = {
        ...state,
        conversationCache: remainingCache,
      };
      break;

    case CLEAR_ALL_CONVERSATIONS:
    case actionTypes.SWITCH_WORKSPACE:
      newState = {
        ...state,
        conversationCache: {},
      };
      break;

    default:
      return state;
  }

  // Save to sessionStorage after state changes
  saveToSessionStorage(newState);
  return newState;
};
